<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import BarChart from 'shared/components/charts/BarChart.vue';
import MetricHint from './MetricHint.vue';

const props = defineProps({
  rows: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const MAX_BARS = 10;

const rankedRows = computed(() =>
  [...props.rows]
    .filter(row => Number(row.conversationsCount) > 0)
    .sort((a, b) => Number(b.conversationsCount) - Number(a.conversationsCount))
    .slice(0, MAX_BARS)
);

const chartData = computed(() => ({
  categories: rankedRows.value.map(row => row.name),
  series: [
    {
      id: 'conversations_count',
      label: t('COMPANY_REPORTS.METRICS.CONVERSATIONS'),
      color: '#902ca3',
      data: rankedRows.value.map(row => ({
        value: Number(row.conversationsCount) || 0,
      })),
    },
  ],
}));

const onItemClick = payload => {
  const index = payload?.pointIndex ?? payload?.index ?? payload?.dataIndex;
  const row = rankedRows.value[index];
  if (!row?.id) return;

  router.push({
    name: 'company_reports_show',
    params: { ...route.params, id: row.id },
    query: route.query,
  });
};
</script>

<template>
  <div>
    <div
      v-if="rankedRows.length"
      class="px-2 py-4 mt-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="px-3 mb-3 text-sm font-medium text-n-slate-12">
        <MetricHint
          :label="$t('COMPANY_REPORTS.CHART_TITLE')"
          :tooltip="$t('COMPANY_REPORTS.TOOLTIPS.CHART')"
        />
      </h3>
      <BarChart
        :data="chartData"
        :aria-label="$t('COMPANY_REPORTS.CHART_ARIA_LABEL')"
        clickable
        @item-click="onItemClick"
      />
    </div>
  </div>
</template>
